import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

import { AppPhase } from '../../../models/communication/app-enums.model';
import { AppEventType, GamePhaseChangeEvent, RoundEndPhaseChangeEvent } from '../../../models/communication/app-events.model';
import { DealerService } from '../../../services/dealer.service';
import { RouletteHistoryService } from '../../../services/roulette-history.service';
import { RouletteService } from '../../../services/roulette.service';

interface WinningNumberDetails {
    color: 'black' | 'green' | 'red' | null;
    backgroundClass: string;
    evenOrOdd: 'Even' | 'Odd' | null;
    half: '1-18' | '19-36' | null;
    third: '1-12' | '13-24' | '25-36' | null;
}

@Component({
    selector: 'app-roulette-winning-number',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './roulette-winning-number.component.html',
    styleUrls: ['./roulette-winning-number.component.scss']
})
export class RouletteWinningNumberComponent implements OnInit {
    winningNumber = 0;
    displayNumber: number | string = 0;
    numberDetails!: WinningNumberDetails;
    isVisible = false;
    private readonly destroyRef = inject(DestroyRef);
    private readonly doubleZero = 37;

    constructor(
        private readonly roulette: RouletteService,
        private readonly dealerService: DealerService,
        private readonly historyService: RouletteHistoryService
    ) {}

    ngOnInit(): void {
        this.calculateNumberDetails();
        this.dealerService
            .getEventsObservable()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((event) => event.name === AppEventType.GamePhaseChange)
            )
            .subscribe((event) => {
                const phaseData = event.data as GamePhaseChangeEvent;
                this.handlePhaseChange(phaseData);
            });
    }

    private handlePhaseChange(phaseData: GamePhaseChangeEvent): void {
        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
        switch (phaseData.phase) {
            case AppPhase.RoundEnd: {
                const roundEndData = phaseData as RoundEndPhaseChangeEvent;
                this.updateWinningNumber(roundEndData.outcomes[0]);
                this.isVisible = true;
                break;
            }
            case AppPhase.BetsOpen:
                this.isVisible = false;
                break;
        }
    }

    private updateWinningNumber(newNumber: number): void {
        this.winningNumber = newNumber;
        this.displayNumber = newNumber === this.doubleZero ? '00' : newNumber;
        this.calculateNumberDetails();

        // Add the new winning number to the history service
        // This will automatically update all components subscribed to the history
        this.historyService.addNumber(newNumber);
    }

    private calculateNumberDetails(): void {
        this.numberDetails = {
            color: this.roulette.getRouletteColor(this.winningNumber),
            backgroundClass: this.roulette.getBackgroundColorClass(this.winningNumber),
            evenOrOdd: this.roulette.isEvenOrOdd(this.winningNumber),
            half: this.roulette.getHalf(this.winningNumber),
            third: this.roulette.getThird(this.winningNumber)
        };
    }
}
